/**
 * DE / EN, above every email draft in the board's pop-ups.
 *
 * The Move modal had this and nothing else did, so a recruiter writing to an
 * English-speaking candidate had to rewrite a German draft by hand in the
 * propose-times, final-round-summary and post-debrief-rejection modals
 * (Theresa, Sep 2026). One component so the control looks and behaves the same
 * in all of them, and so a fourth draft cannot ship without it by accident.
 *
 * It does NOT translate anything. Each caller re-fetches its draft in the chosen
 * language — from the Email Library's own `de`/`en` copy where there is one.
 * Translating on the client would put words in front of a candidate that TA
 * never approved, which is the whole reason the Library owns this wording.
 */
import { cn } from '../../../../shared';

const LANGS = ['de', 'en'] as const;
export type DraftLanguage = (typeof LANGS)[number];

export function LanguageToggle({
  value,
  onChange,
  hasEdits = false,
  disabled = false,
  label = 'Language',
}: {
  value: DraftLanguage;
  /** Called only for an ACTUAL change — a click on the active language is a no-op. */
  onChange: (next: DraftLanguage) => void;
  /**
   * The recruiter has edited the draft by hand. Switching re-drafts and loses
   * those edits, so it is confirmed first — silently discarding someone's
   * rewrite is the kind of thing that stops a control being trusted.
   */
  hasEdits?: boolean;
  /**
   * Locked while the caller is submitting.
   *
   * Switching mid-send re-drafts underneath a request already in flight, so what
   * goes out is not what was on screen when confirm was pressed. Callers that
   * had raw buttons guarded this themselves; the shared control has to keep it.
   */
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <div className="flex overflow-hidden rounded-lg border border-gray-200 text-xs">
        {LANGS.map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={value === l}
            disabled={disabled}
            onClick={() => {
              if (l === value) return;
              if (
                hasEdits &&
                !window.confirm('This will re-draft the email and discard your edits. Continue?')
              ) {
                return;
              }
              onChange(l);
            }}
            className={cn(
              'px-3 py-1.5 font-medium',
              value === l ? 'bg-[#0072EA] text-white' : 'bg-white text-gray-600 hover:bg-gray-50',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}
